import React, { Component } from 'react'
import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import Pagination from 'components/Pagination';
import {
  Button,
  Checkbox,
  Alert, Intent, FormGroup
} from '@blueprintjs/core';
import DateTimeInput from 'components/DateTimeInput';
import FormDrawer from 'components/FormDrawer';
import { FormSection, Choice, FieldError, Toggle } from 'components/FormBits';
import Footer from '../Footer';
import moment from 'moment';
import Toast from 'helpers/toaster';
import qs from 'qs';
import { currentTime } from 'helpers/time';
import * as DataList from 'components/DataList';
import { When } from 'components/Tables/ScheduleList';

// Column and card layouts, shared with the page's loading skeleton. The
// select-all checkbox lives in the summary line so it works on cards too.
export const TIMESHEET_COLUMNS = [
  { area: 'select', label: '', width: '28px', bone: 'none' },
  { area: 'job', label: 'Job', width: 'minmax(130px, 1fr)', bone: 'short' },
  { area: 'project', label: 'Project', width: '110px', bone: 'single', icon: 'projects' },
  { area: 'team', label: 'Technician', width: 'minmax(180px, 1.2fr)', bone: 'person' },
  { area: 'client', label: 'Client', width: 'minmax(180px, 1.3fr)', icon: 'map-marker' },
  { area: 'when', label: 'Start & end', width: '150px', bone: 'short', icon: 'time' },
  { area: 'logged', label: 'Time logged', width: '110px', bone: 'short' },
  { area: 'status', label: 'Status', width: '110px', bone: 'pill' },
  { area: 'created', label: 'Created', width: '110px', bone: 'short' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

export const TIMESHEET_CARDS = {
  columns: 'auto minmax(0, 1fr) auto auto',
  areas: [
    'select job status actions',
    'team team team team',
    'when when when when',
    'client client client client',
    'project project project project',
    'created created logged logged',
  ],
  footer: ['created', 'logged'],
};

export const TIMESHEET_MIN_TABLE_WIDTH = 1240;

const TIMESHEET_FILTERS = {
  status: 'status',
  service_type_id: 'service type',
  technician_id: 'technician',
  project_id: 'project',
  client_id: 'client',
  location_id: 'location',
};

const TIMELOG_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', tone: 'grey' },
  { value: 'approved', label: 'Approved', tone: 'green' },
  { value: 'rejected', label: 'Rejected', tone: 'red' },
];

const initialsOf = (name = '') =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

// "1 hr 30 min", "45 min", "2 hrs".
const lengthLabel = (minutes) => {
  if (minutes === null || minutes < 0) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts = [];
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
  if (mins || !hours) parts.push(`${mins} min`);
  return parts.join(' ');
};

const STATUS_TONES = { approved: 'green', rejected: 'red', draft: 'grey' };

const TimelogStatus = ({ status }) => (
  <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>
    {status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'No status'}
  </DataList.Pill>
);

const dateLabel = (date) => {
  const time = currentTime(date);
  return time.format(time.isSame(currentTime(), 'year') ? 'MMM D' : 'MMM D, YYYY');
};


@inject('store', 'timesheetStore', 'viewStore') @observer
class TimesheetsTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable isEditDialogVisible = false;
  @observable timelog;
  @observable status;
  @observable over_time;
  @observable editErrors = null;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        timesheetSearch
      }
    } = this.props;
    this.props.viewStore.timesheetSearch = {
      ...timesheetSearch,
      page: newPage
    };
    this.props.viewStore.timesheets();
  }

  // Keep the date range, drop every other filter, and remount the filter bar.
  clearFilters = () => {
    const { viewStore } = this.props;
    const { timesheetSearch } = viewStore;
    const { start_time, end_time } = timesheetSearch.where || {};
    viewStore.timesheetSearch = {
      ...timesheetSearch,
      where: { start_time, end_time },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.timesheets();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.timesheetStore.deleteTimelog(this.timelog)
      .then(this.onSuccessfulDeleteTimelog.bind(this))
      .catch(this.onFailedTimelogDelete.bind(this))
  }

  onSuccessfulDeleteTimelog(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Timelog deleted successfully!', intent: Intent.SUCCESS })
    this.timelog = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedTimelogDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete timelog!', intent: Intent.DANGER })
  }

  @action
  selectAllTimelogs = (event) => {
    let {
      timesheetStore,
      timesheetStore: {
        timelogs
      }
    } = this.props;
    timesheetStore.allEntriesSelected = event.target.checked;
    timelogs.objects.forEach((timelog) => {
      timelog.checked = event.target.checked;
    })
  }

  selectTimeEntry = (event, timelog) => {
    timelog.checked = event.target.checked;
  }

  handleTimeLogClick(event, timelog) {
    switch (event.label) {
      case 'Edit':
        this.isEditDialogVisible = true;
        this.timelog = timelog;
        // Start clean: a status or overtime picked while editing another
        // entry must not carry over to this one.
        this.status = null;
        this.over_time = null;
        this.editErrors = null;
        // Edits go straight into the row; Cancel puts these back.
        this.editSnapshot = {
          start_time: timelog.attributes.start_time,
          end_time: timelog.attributes.end_time,
          status: timelog.attributes.status,
          is_overtime: timelog.attributes.is_overtime,
        };
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.timelog = timelog;
        break;
      default:
    }
  }

  handleCloseTimelogDailog = () => {
    if (this.editSnapshot && this.timelog) {
      Object.assign(this.timelog.attributes, this.editSnapshot);
    }
    this.editSnapshot = null;
    this.isEditDialogVisible = false;
  }

  updateTimelog() {
    if(!this.status){
      this.status = this.timelog.attributes.status;
    }else{
      this.timelog.attributes.status = this.status;
    }

    if(!this.over_time){
      this.over_time = this.timelog.attributes.is_overtime;
    }else{
      this.timelog.attributes.is_overtime = this.over_time;
    }
    
    this.editErrors = this.editProblems();
    const first = ['end', 'status'].find((key) => this.editErrors[key]);
    if (first) {
      const el = document.getElementById(first === 'end' ? 'timelog-edit-end' : 'timelog-edit-status');
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const focusable = el.querySelector('input, button');
        if (focusable) focusable.focus();
      }
      return;
    }

    this.props.timesheetStore.update(this.timelog)
      .then(this.onSucessfulUpdateTimelog.bind(this))
      .catch(this.FailUpdateTimelog.bind(this))
  }

  onSucessfulUpdateTimelog(response) {
    this.editSnapshot = null;
    this.isEditDialogVisible = false;
    Toast.show({ icon: 'thumbs-up', message: 'Timelog updated successfully!', intent: Intent.SUCCESS })
  }

  FailUpdateTimelog(error) {
    this.isEditDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Failed to update timelog', intent: Intent.DANGER })
  }

  onStartTimeChange = (time) => {
    this.timelog.attributes.start_time = time;
    this.revalidateEdit();
  }

  onEndTimeChange = (time) => {
    this.timelog.attributes.end_time = time;
    this.revalidateEdit();
  }

  // Problems with the Edit time log panel, shown under the fields.
  editProblems() {
    const attributes = this.timelog.attributes;
    const errors = {};
    if (moment(attributes.start_time) > moment(attributes.end_time)) {
      errors.end = 'Ends before it starts. Pick a later end time.';
    }
    if (!(this.status || attributes.status)) {
      errors.status = 'Choose a status.';
    }
    return errors;
  }

  revalidateEdit() {
    if (this.editErrors) this.editErrors = this.editProblems();
  }

  // Status chips: kept aside and applied on Save (Cancel leaves the row alone).
  handleStatusChange = (value) => {
    this.status = value;
    this.revalidateEdit();
  }

  handleOvertimeChange = (checked) => {
    this.over_time = checked;
    this.timelog.attributes.is_overtime = checked
  }

  render() {
    let {
      viewStore,
      viewStore: {
        jobTab: {
          section
        }
      },
      timesheetStore: {
        timelog: {
          isLoading
        },
        timelogs: {
          objects,
          meta
        },
        allEntriesSelected
      }
    } = this.props;
    if (objects.length === 0) {
      const where = viewStore.timesheetSearch.where || {};
      const start = where.start_time && where.start_time.gte;
      const end = where.end_time && where.end_time.lte;
      const range = start && end
        ? `${dateLabel(moment.unix(parseInt(start, 10)))} and ${dateLabel(moment.unix(parseInt(end, 10)))}`
        : null;
      const filters = DataList.activeFilters(where, TIMESHEET_FILTERS);
      const onlyDates = filters.length === 0 && range;
      return (
        <DataList.Empty
          noun="time entries"
          filters={filters}
          title={onlyDates ? `No time logged between ${range}` : undefined}
          message={onlyDates ? 'Nothing was logged in this date range. Pick other dates above.' : undefined}
          onClearFilters={this.clearFilters}
        />
      );
    }

    const count = meta.total_records || objects.length;

    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={TIMESHEET_COLUMNS}
          cards={TIMESHEET_CARDS}
          minTableWidth={TIMESHEET_MIN_TABLE_WIDTH}
          total={(
            <React.Fragment>
              <Checkbox
                className="dl-raised"
                style={{ display: 'inline-block', margin: '0 10px 0 0', verticalAlign: 'middle' }}
                onChange={this.selectAllTimelogs.bind(this)}
                checked={allEntriesSelected}
                aria-label="Select all time entries on this page"
              />
              {count}
            </React.Fragment>
          )}
          noun={count === 1 ? 'time entry' : 'time entries'}
          note={DataList.sortNote(viewStore.timesheetSearch.order)}
        >
          {objects.map((timelog) => {
            const attributes = timelog.attributes;
            const user = attributes.user;
            const userLink = user && `/dashboard/users/${user.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`;
            return (
              <DataList.Row key={timelog.id}>
                <DataList.Cell area="select">
                  <Checkbox
                    className="dl-raised"
                    style={{ margin: 0 }}
                    checked={timelog.checked}
                    onChange={(event) => { this.selectTimeEntry(event, timelog) }}
                    aria-label="Select time entry"
                  />
                </DataList.Cell>
                <DataList.Cell area="job">
                  {attributes.job ? (
                    <DataList.Lines
                      primary={`#${attributes.job.event_number}`}
                      href={`/dashboard/jobs/${attributes.job.id}/${section}`}
                      secondary={attributes.service_type}
                    />
                  ) : (
                    <DataList.Lines primary="No job" strong={false} secondary={attributes.service_type} />
                  )}
                </DataList.Cell>
                <DataList.Cell area="project">
                  {attributes.project ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Project </DataList.CardOnly>{attributes.project.number}</React.Fragment>}
                      href={`/dashboard/projects/${attributes.project.id}/show?${qs.stringify(viewStore.projectShowSearch, { encode: false })}`}
                      title={`Project ${attributes.project.number}`}
                    />
                  ) : (
                    <DataList.Muted>No project</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="team">
                  <DataList.Raised
                    as="a"
                    href={userLink}
                    style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
                  >
                    <DataList.Person name={attributes.technician_name} secondary={user && user.email} />
                  </DataList.Raised>
                </DataList.Cell>
                <DataList.Cell area="client">
                  <DataList.Lines primary={attributes.client_name} secondary={attributes.location_address} />
                </DataList.Cell>
                <DataList.Cell area="when">
                  <When start={attributes.start_time} end={attributes.end_time} />
                </DataList.Cell>
                <DataList.Cell area="logged">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Logged </DataList.CardOnly>{attributes.formatted_duration}</React.Fragment>}
                    secondary={attributes.is_overtime ? 'Overtime' : null}
                    title={attributes.formatted_duration}
                  />
                </DataList.Cell>
                <DataList.Cell area="status">
                  <TimelogStatus status={attributes.status} />
                </DataList.Cell>
                <DataList.Cell area="created">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Created </DataList.CardOnly>{dateLabel(attributes.created_at)}</React.Fragment>}
                    secondary={currentTime(attributes.created_at).format('h:mm A')}
                    strong={false}
                    title={currentTime(attributes.created_at).format('MMM D, YYYY h:mm A')}
                  />
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit time entry', onClick: () => this.handleTimeLogClick({ label: 'Edit' }, timelog) },
                      { icon: 'trash', label: 'Delete time entry', intent: 'danger', onClick: () => this.handleTimeLogClick({ label: 'Delete' }, timelog) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete time entry"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <strong>Delete this time entry?</strong>
          <p>It’s removed from the timesheet and can’t be brought back.</p>
        </Alert>
        
        {this.timelog && this.timelog.attributes.id && (() => {
          const attributes = this.timelog.attributes;
          const errors = this.editErrors || {};
          const startAt = attributes.start_time && moment(attributes.start_time);
          const endAt = attributes.end_time && moment(attributes.end_time);
          const minutes = startAt && endAt ? endAt.diff(startAt, 'minutes') : null;
          const length = lengthLabel(minutes);
          const job = attributes.job && attributes.job.event_number ? `Job #${attributes.job.event_number}` : null;
          return (
            <FormDrawer
              title="Edit time log"
              icon="time"
              size="min(760px, 100vw)"
              isOpen={this.isEditDialogVisible}
              onClose={this.handleCloseTimelogDailog}
              busy={!!isLoading}
              footer={
                <>
                  <Button text="Cancel" onClick={this.handleCloseTimelogDailog} disabled={!!isLoading} />
                  <Button
                    intent={Intent.PRIMARY}
                    icon="floppy-disk"
                    text="Save changes"
                    loading={isLoading}
                    onClick={this.updateTimelog.bind(this)}
                  />
                </>
              }
            >
              <FormSection title="When" hint="When the work started and ended.">
                <div className="tl-summary">
                  <span className="tl-summary__avatar" aria-hidden="true">{initialsOf(attributes.technician_name)}</span>
                  <span className="tl-summary__text">
                    <span className="tl-summary__name">{attributes.technician_name || 'Technician'}</span>
                    <span className="tl-summary__meta">
                      {[startAt && startAt.format('ddd, MMM D, YYYY'), job].filter(Boolean).join(', ')}
                    </span>
                  </span>
                  {length && (
                    <span className={`tl-summary__length${errors.end ? ' is-invalid' : ''}`} aria-live="polite">{length}</span>
                  )}
                </div>
                <div className="fb-grid">
                  <div id="timelog-edit-start">
                    <FormGroup label="Started" labelInfo={<span className="fb-required">*</span>}>
                      <DateTimeInput
                        placeholder={'Start'}
                        defaultValue={moment(attributes.start_time)}
                        onChange={this.onStartTimeChange.bind(this)}
                      />
                    </FormGroup>
                  </div>
                  <div className={errors.end ? 'has-error' : undefined} id="timelog-edit-end">
                    <FormGroup label="Ended" labelInfo={<span className="fb-required">*</span>}>
                      <DateTimeInput
                        placeholder={'End'}
                        defaultValue={moment(attributes.end_time)}
                        onChange={this.onEndTimeChange.bind(this)}
                      />
                      <FieldError>{errors.end}</FieldError>
                    </FormGroup>
                  </div>
                </div>
                <Toggle
                  id="timelog-edit-overtime"
                  label="Overtime"
                  hint="Counts as overtime on the timesheet."
                  checked={Boolean(attributes.is_overtime)}
                  onChange={this.handleOvertimeChange}
                />
              </FormSection>

              <FormSection title="Review" hint="Approved time goes to payroll.">
                <div id="timelog-edit-status">
                  <Choice
                    label="Status"
                    required
                    value={this.status || attributes.status}
                    onChange={this.handleStatusChange}
                    options={TIMELOG_STATUS_OPTIONS}
                    error={errors.status}
                  />
                </div>
              </FormSection>
            </FormDrawer>
          );
        })()}
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
      </React.Fragment>
    )
  }
}

export default TimesheetsTable;